import React, { useEffect, useRef, useState } from "react";
import { fetchYoutubeVideos } from "../../services/youtubeVideos";

const SOCIAL = {
  facebook: "https://www.facebook.com/tenderapex/",
  instagram: "https://www.instagram.com/tenderapex/",
  linkedin: "https://www.linkedin.com/company/tender-apex/",
  x: "https://x.com/tenderapex",
  youtube: "https://www.youtube.com/@TenderApex",
};

const extractYoutubeVideoId = (video) => {
  const directId = String(video?.youtube_video_id || "").trim();
  if (directId) return directId;

  const url = String(video?.youtube_url || "").trim();
  if (!url) return "";

  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) {
      return parsed.pathname.replace("/", "").trim();
    }
    const fromQuery = parsed.searchParams.get("v");
    if (fromQuery) return fromQuery.trim();
    const embedMatch = parsed.pathname.match(/\/embed\/([^/]+)/);
    if (embedMatch?.[1]) return embedMatch[1].trim();
  } catch {
    return "";
  }
  return "";
};

const loadScriptOnce = (src, id) => {
  if (typeof document === "undefined") return Promise.resolve(null);
  const existing = document.getElementById(id);
  if (existing) {
    return existing.dataset.loaded === "1"
      ? Promise.resolve(existing)
      : new Promise((resolve, reject) => {
          existing.addEventListener("load", () => resolve(existing), { once: true });
          existing.addEventListener("error", reject, { once: true });
        });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "1";
      resolve(script);
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

const PlatformCard = ({ icon, name, href, children }) => (
  <article className="ft-embed-card">
    <div className="ft-embed-label">
      {icon}
      <span>{name}</span>
      <a href={href} target="_blank" rel="noopener noreferrer">
        View
      </a>
    </div>
    <div className="ft-embed-frame">{children}</div>
  </article>
);

const SocialFallback = ({
  href,
  brand,
  title,
  handle,
  blurb,
  cta,
  accent,
}) => (
  <a
    className="ft-social-fallback"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{ "--sf-accent": accent }}
  >
    <div className="ft-social-fallback-brand">{brand}</div>
    <div className="ft-social-fallback-title">{title}</div>
    <div className="ft-social-fallback-handle">{handle}</div>
    <p className="ft-social-fallback-blurb">{blurb}</p>
    <span className="ft-social-fallback-cta">{cta}</span>
  </a>
);

const SocialEmbeds = () => {
  const [youtubeId, setYoutubeId] = useState("");
  const [youtubeTitle, setYoutubeTitle] = useState("Latest YouTube video");
  const [fbWidth, setFbWidth] = useState(340);
  const [fbReady, setFbReady] = useState(false);
  const [fbFailed, setFbFailed] = useState(false);

  const fbWrapRef = useRef(null);
  const fbPageRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    fetchYoutubeVideos()
      .then((videos) => {
        if (!mounted) return;
        const first = (Array.isArray(videos) ? videos : []).find((video) =>
          extractYoutubeVideoId(video)
        );
        if (!first) return;
        setYoutubeId(extractYoutubeVideoId(first));
        setYoutubeTitle(String(first.title || "Latest YouTube video").trim());
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  // Measure Facebook plugin width from its card
  useEffect(() => {
    const node = fbWrapRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const apply = () => {
      const width = Math.max(180, Math.min(500, Math.floor(node.clientWidth || 340)));
      setFbWidth(width);
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Facebook SDK page plugin
  useEffect(() => {
    let cancelled = false;
    let failTimer;

    const bootFacebook = async () => {
      try {
        if (!window.FB) {
          window.fbAsyncInit = () => {
            if (cancelled) return;
            window.FB.init({
              xfbml: true,
              version: "v21.0",
            });
            window.FB.XFBML.parse(fbWrapRef.current || undefined);
            setFbReady(true);
          };

          await loadScriptOnce(
            "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v21.0",
            "facebook-jssdk"
          );

          // If SDK already present from a previous visit
          if (!cancelled && window.FB?.XFBML) {
            window.FB.XFBML.parse(fbWrapRef.current || undefined);
            setFbReady(true);
          }
        } else if (!cancelled) {
          window.FB.XFBML.parse(fbWrapRef.current || undefined);
          setFbReady(true);
        }
      } catch {
        if (!cancelled) setFbFailed(true);
      }

      // Page plugin often stays blank when cookies / login block it
      failTimer = window.setTimeout(() => {
        if (cancelled) return;
        const iframe = fbWrapRef.current?.querySelector("iframe");
        const usable =
          iframe &&
          iframe.offsetHeight > 40 &&
          iframe.getAttribute("src")?.includes("facebook.com");
        if (!usable) setFbFailed(true);
      }, 4500);
    };

    bootFacebook();
    return () => {
      cancelled = true;
      if (failTimer) window.clearTimeout(failTimer);
    };
  }, [fbWidth]);

  // Re-parse Facebook when width changes after first ready
  useEffect(() => {
    if (!fbReady || !window.FB?.XFBML || !fbWrapRef.current) return;
    window.FB.XFBML.parse(fbWrapRef.current);
  }, [fbReady, fbWidth]);

  return (
    <section className="ft-social-embeds" aria-label="Latest social posts">
      <div className="container">
        <div className="ft-social-embeds-head">
          <h5 className="ft-col-hd" style={{ marginBottom: 8 }}>
            Latest on social
          </h5>
          <p className="ft-social-embeds-sub">
            One recent update from each TenderApex channel
          </p>
        </div>

        <div className="ft-social-embeds-grid">
          <PlatformCard
            name="Instagram"
            href={SOCIAL.instagram}
            icon={<i className="fab fa-instagram" aria-hidden="true" />}
          >
            <iframe
              title="TenderApex on Instagram"
              src="https://www.instagram.com/tenderapex/embed"
              loading="lazy"
              allow="encrypted-media"
            />
          </PlatformCard>

          <PlatformCard
            name="Facebook"
            href={SOCIAL.facebook}
            icon={<i className="fab fa-facebook-f" aria-hidden="true" />}
          >
            <div className="ft-embed-fb-wrap" ref={fbWrapRef}>
              {!fbFailed && (
                <div
                  ref={fbPageRef}
                  className="fb-page"
                  data-href={SOCIAL.facebook}
                  data-tabs="timeline"
                  data-width={String(fbWidth)}
                  data-height="380"
                  data-small-header="true"
                  data-adapt-container-width="false"
                  data-hide-cover="false"
                  data-show-facepile="false"
                />
              )}
              {fbFailed && (
                <SocialFallback
                  href={SOCIAL.facebook}
                  brand="Facebook"
                  title="Tender Apex"
                  handle="facebook.com/tenderapex"
                  blurb="See the latest TenderApex updates, tips, and tender news on Facebook."
                  cta="Open latest posts →"
                  accent="#1877f2"
                />
              )}
            </div>
          </PlatformCard>

          <PlatformCard
            name="LinkedIn"
            href={SOCIAL.linkedin}
            icon={<i className="fab fa-linkedin-in" aria-hidden="true" />}
          >
            {/* LinkedIn does not provide a public company-feed embed; use a branded card */}
            <SocialFallback
              href={SOCIAL.linkedin}
              brand="LinkedIn"
              title="Tender Apex"
              handle="linkedin.com/company/tender-apex"
              blurb="Follow TenderApex on LinkedIn for company updates and industry insights."
              cta="View latest posts →"
              accent="#0a66c2"
            />
          </PlatformCard>

          <PlatformCard
            name="X"
            href={SOCIAL.x}
            icon={
              <span className="ft-embed-x" aria-hidden="true">
                𝕏
              </span>
            }
          >
            {/* X no longer renders a public timeline embed without a login wall */}
            <SocialFallback
              href={SOCIAL.x}
              brand="X"
              title="Tender Apex"
              handle="@tenderapex"
              blurb="Catch the latest TenderApex posts and quick tender tips on X."
              cta="Open latest posts →"
              accent="#e7e9ea"
            />
          </PlatformCard>

          <PlatformCard
            name="YouTube"
            href={SOCIAL.youtube}
            icon={<i className="fab fa-youtube" aria-hidden="true" />}
          >
            <div className="ft-embed-frame--youtube-inner">
              {youtubeId ? (
                <iframe
                  title={youtubeTitle}
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <SocialFallback
                  href={SOCIAL.youtube}
                  brand="YouTube"
                  title="Tender Apex"
                  handle="@TenderApex"
                  blurb="Watch TenderApex tutorials and tender intelligence videos."
                  cta="Watch on YouTube →"
                  accent="#ff0033"
                />
              )}
            </div>
          </PlatformCard>
        </div>
      </div>
    </section>
  );
};

export default SocialEmbeds;
